<template>
  <div>
    <div class="w-full p-4 border rounded-md bg-gray-50 mb-3 dark:bg-gray-900">
      <div class="flex space-x-4">
        <o-field-group
          v-if="isCustomerTypeSingle"
          :label="$t('general.sendEmail')"
          :info="isMove ? $t('components.reservation.create.confirm.sendMovedEmail') : $t('components.reservation.create.confirm.sendConfirmationEmail')"
          class="w-full md:w-1/2"
        >
          <toggle-field v-model="_staging.sendEmail" />
        </o-field-group>


        <o-field-group
          v-if="isMove && _staging && (_staging.reservation || _staging.reservationCustomer)"
          :label="$t('reservation.resetReminders')"
          :info="$t('reservation.resetRemindersInfo')"
          class="w-full md:w-1/2"
        >
          <toggle-field v-model="_staging.resetReminder" />
        </o-field-group>

        <template v-if="isCustomerTypeGroup">
          <o-field-group :label="$t('general.maxCustomers')" :info="$t('reservation.maxCustomersInfo')" class="w-full md:w-1/2">
            <number-field v-model="_staging.customerMax" />
          </o-field-group>

          <o-field-group
            :label="isCourseSession ? 'Individually bookable online' : 'Allow online'"
            :info="isCourseSession ? 'Allow customers to book this class online without joining course' : 'Allow customer to book online (book.app, widgets, bots)'"
            class="w-full md:w-1/2"
          >
            <toggle-field v-model="_staging.allowInternet" />
          </o-field-group>
        </template>
      </div>
    </div>
    <div v-if="isCustomerTypeSingle" class="w-full h-full p-4 border rounded-md bg-gray-50 mb-3 dark:bg-gray-900">
      <o-alert v-if="isClassFull" :title="$t('components.reservation.create.confirm.full')" show variant="warning" class="mb-3 text-sm">
        <p>
          {{ $t('components.reservation.create.confirm.fullInfo') }}
        </p>
      </o-alert>

      <div class="flex flex-wrap md:flex-nowrap items-end space-x-3 ">
        <div class="w-full md:w-1/2 text-gray-600 text-sm dark:text-gray-300">
          <div class="mb-4">
            <p class="text-sm text-gray-700 dark:text-gray-200">
              {{ $t('general.startTime') }}
            </p>
            <p>
              {{ timeLabel }}
            </p>
          </div>
          <div class="mb-4">
            <p class="text-sm text-gray-700 dark:text-gray-200">
              {{ $t('general.date') }}
            </p>
            <p>
              {{ dateLabel }}
            </p>
          </div>
          <div>
            <p class="text-sm text-gray-700 dark:text-gray-200">
              {{ $t('general.customer') }}
            </p>
            <div>
              <template v-if="staging.customer">
                <p>
                  {{ staging.customer.displayName || `${ staging.customer.firstName} ${staging.customer.lastName}` }}
                </p>
                <p>
                  {{ staging.customer.phone }}
                </p>
                <p>
                  {{ staging.customer.email }}
                </p>
              </template>
              <template v-else>
                <p>
                  -
                </p>
              </template>
            </div>
          </div>
        </div>
        <div class="w-full md:w-1/2">
          <div>
            <o-description-details
              v-for="(service, serviceKey) in services"
              :key="serviceKey"
              :term="service.service.title"
              term-col="sm:col-span-3"
              definition-col="sm:col-span-3"
              align-right
            >
              <p class="text-sm text-gray-700 dark:text-gray-200">
                <span v-if="serviceDiscount(service)" class="line-through mr-2">{{ $currency.format(service.price) }}</span>
                <span class="font-semibold">{{ $currency.format(service.price - serviceDiscount(service)) }}</span>
              </p>
            </o-description-details>
          </div>

          <div v-if="discount" class="border-t">
            <o-description-details :term="$t('general.subtotal')" align-right term-col="sm:col-span-3" definition-col="sm:col-span-3" class="-mb-2">
              <p class="text-gray-700 dark:text-gray-200">
                {{ $currency.format(price) }}
              </p>
            </o-description-details>

            <o-description-details :term="$t('general.discount')" align-right term-col="sm:col-span-3" definition-col="sm:col-span-3">
              <p class="text-green-600 dark:text-green-400">
                {{ $currency.format(discount) }}
              </p>
            </o-description-details>
          </div>

          <o-description-details :term="$t('general.total')" align-right class="border-t" term-col="sm:col-span-3" definition-col="sm:col-span-3">
            <p class="font-semibold text-lg text-red-500">
              {{ $currency.format(total) }}
            </p>
          </o-description-details>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  OAlert,
  ODescriptionDetails,
} from '@/components/elements'

import {
  ToggleField,
  SelectField,
  NumberField,
} from '@/components/fields'

const { $date, $auth, $currency } = useNuxtApp()

interface Props {
  staging: Record<string, any>
  isActive: boolean
  courseRun?: object | null
}

interface Emits {
  (e: 'update:staging', value: any): void
}

const props = withDefaults(defineProps<Props>(), {
  courseRun: null,
})

const emit = defineEmits<Emits>()

const loading = ref(false)

const _staging = computed({
  get () {
    return props.staging
  },
  set (value) {
    emit('update:staging', value)
  },
})

const validation = computed(() => {
  return _staging.value.validation?.data ?? _staging.value.validation
})

const services = computed(() => {
  const svc = validation.value?.services ?? props.staging.services ?? []

  return svc
    .filter(({ service }: any) => !!service)
})

const price = computed(() => {
  return validation.value?.price
})

const discount = computed(() => {
  return validation.value?.discount
})

const total = computed(() => {
  if (validation.value) {
    return price.value - discount.value
  }

  return services.value.reduce((acc: number, service: any) => {
    const price = Number(service.price) || 0

    return acc + price - serviceDiscount(service)
  }, 0)
})

const serviceDiscount = (service: any) => {
  if (service.discountTotal != null) {
    return Number(service.discountTotal) || 0
  }

  return Number(service.discount) || 0
}

const customerLabel = computed(() => {
  if (props.staging.customer) {
    return props.staging.customer.displayName
  }

  return '-'
})

const timeLabel = computed(() => {
  if (props.staging.availabilitySlot && props.staging.availabilitySlot.start) {
    return $date.minutes(props.staging.availabilitySlot.start)
  }

  return '-'
})

const dateLabel = computed(() => {
  if (props.staging.availabilitySlot && props.staging.availabilitySlot.date) {
    const utc = $date.utc(props.staging.availabilitySlot.date)
    return $date.format(utc, $date.presets.long)
  }

  return '-'
})

const currencySymbol = computed(() => {
  return $auth.info.location.currencySymbol
})

const isCustomerTypeGroup = computed(() => {
  return props.staging.isCustomerTypeGroup
})

const isCustomerTypeSingle = computed(() => {
  return props.staging.isCustomerTypeSingle
})

const isAvailabilityComplete = computed(() => {
  return props.staging.isAvailabilityComplete
})

const isMove = computed(() => {
  return !!props.staging.move
})

const isClassFull = computed(() => {
  const slot = props.staging?.availabilitySlot
  const service = slot?.services?.[0]

  if (service && service.customerMax) {
    const customerMax = service.customerMax
    const customerCount = service.customerCount
    return customerCount >= customerMax
  }

  return false
})

const isCourseSession = computed(() => {
  return !!props.courseRun
})
</script>
